import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';

/**
 * DepartmentCollaborationBar
 * - Horizontal bar chart for department collaborations.
 * - 왼쪽 50%: 학과명, 오른쪽 50%: 차트 영역
 */
export default function DepartmentCollaborationBar({
  items: externalItems = null,
  max = null, // null이면 데이터 최대값 기준으로 자동 계산
  barHeight = 26,
  barGap = 18,
  leftLabelWidth, // 안 넘기면 자동으로 50% 계산
  departmentCode = null,
  type = 'interdepartmental', // 'interdepartmental' or 'institutional'
}) {
  // API에서 데이터 가져오기 (externalItems가 없을 때만)
  const [collaborationData, setCollaborationData] = useState(null);
  const [collaborationLoading, setCollaborationLoading] = useState(false);
  const [collaborationErr, setCollaborationErr] = useState(null);

  useEffect(() => {
    // externalItems가 제공되면 API 호출하지 않음
    if (externalItems !== null) {
      return;
    }

    // departmentCode가 없으면 API 호출하지 않음
    if (!departmentCode) {
      return;
    }

    let alive = true;
    async function fetchCollaboration() {
      setCollaborationLoading(true);
      setCollaborationErr(null);
      try {
        const endpoint = `/rm/v1/department/${encodeURIComponent(departmentCode)}/collaboration?type=${type}`;
        const response = await get(endpoint);
        if (alive) {
          if (response?.status && response?.result) {
            setCollaborationData(response.result);
          } else {
            setCollaborationErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setCollaborationData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setCollaborationErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setCollaborationLoading(false);
        }
      }
    }
    fetchCollaboration();
    return () => { alive = false; };
  }, [externalItems, departmentCode, type]);

  // 데이터 처리: externalItems 또는 API 데이터 사용
  const items = useMemo(() => {
    if (externalItems !== null && Array.isArray(externalItems)) {
      return externalItems;
    }
    if (collaborationData && Array.isArray(collaborationData)) {
      return collaborationData;
    }
    return null;
  }, [externalItems, collaborationData]);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const draw = () => {
      el.innerHTML = '';

      // 로딩 중이면 그리지 않음
      if (externalItems === null && collaborationLoading) {
        return;
      }

      const data = Array.isArray(items) && items.length ? items : [];
      if (!data.length) {
        return;
      }

      // x축 최대값: (명시된 max가 없으면) 가장 큰 값의 10% 여유
      const rawMax = data.reduce((acc, d) => {
        const v = Number(d?.value) || 0;
        return v > acc ? v : acc;
      }, 0);
      let domainMax = max != null ? Number(max) : Math.max(1, rawMax * 1.1);
      if (!Number.isFinite(domainMax) || domainMax <= 0) domainMax = 1;

      const width = el.clientWidth || 800;

      // 👉 왼쪽 라벨 영역을 20%로 줄임
      const labelColWidth = leftLabelWidth != null ? leftLabelWidth : width * 0.20;

      const top = 16;
      const bottom = 40; // x축 숫자
      const right = 4;
      const margin = { top, right, bottom, left: labelColWidth };

      const n = data.length;
      const innerH = n > 0 ? n * barHeight + (n - 1) * barGap : 0;
      const height = innerH + top + bottom;

      const svg = d3
        .select(el)
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`);

      const innerW = Math.max(0, width - margin.left - margin.right);

      // 플롯 그룹 (오른쪽 차트 50% 영역)
      const g = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleLinear().domain([0, domainMax]).range([0, innerW]);

      const yPos = (index) => index * (barHeight + barGap);

      // 세로 그리드 (차트 영역 내 틱만 사용)
      const gridVals = d3.ticks(0, domainMax, 5);

      g.append('g')
        .selectAll('line')
        .data(gridVals)
        .enter()
        .append('line')
        .attr('x1', (d) => x(d))
        .attr('x2', (d) => x(d))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#E4E8F1');

      // x축 숫자
      g.append('g')
        .attr('transform', `translate(0,${innerH})`)
        .call(
          d3
            .axisBottom(x)
            .tickValues(gridVals)
            .tickFormat(d3.format('d'))
        )
        .call((gx) => {
          gx.selectAll('.domain').remove();
          gx.selectAll('text').attr('fill', '#8D9AAF').style('font-size', 11);
          gx.selectAll('line').remove();
        });

      // Tooltip 생성 (type별 고유 클래스로 인스턴스 간 충돌 방지)
      const tooltipClass = `department-collaboration-tooltip-${type}`;
      d3.selectAll(`.${tooltipClass}`).remove();
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', tooltipClass)
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.85)')
        .style('color', 'white')
        .style('padding', '8px 12px')
        .style('border-radius', '6px')
        .style('font-size', '13px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 10000)
        .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)');

      // 막대
      const barColor = '#89A7F1';
      g.append('g')
        .selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', 0)
        .attr('y', (_, i) => yPos(i))
        .attr('height', barHeight)
        .attr('rx', 4)
        .attr('fill', barColor)
        .attr('opacity', 0.9)
        .style('cursor', 'pointer')
        .attr('width', (d) => x(Math.max(0, Math.min(domainMax, Number(d.value) || 0))))
        .on('mouseenter', function(event, d) {
          const label = String(d.label || '').replace(/\n/g, ' ');
          const value = Number(d.value) || 0;
          tooltip
            .html(`<div>${label}</div><div style="margin-top:4px;font-size:11px;opacity:0.9;">${value}</div>`)
            .style('opacity', 1);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseleave', function() {
          tooltip.style('opacity', 0);
        });

      // 왼쪽 학과 라벨들 (20% 영역 안에서, 긴 텍스트는 앞 글자 기준으로 ... 표시)
      const labelsGroup = svg
        .append('g')
        .attr('transform', `translate(${labelColWidth - 6},${margin.top})`);

      // 텍스트 너비 측정을 위한 임시 텍스트 요소
      const tempText = svg.append('text')
        .style('font-size', 13)
        .style('font-family', 'system-ui, -apple-system, sans-serif')
        .style('visibility', 'hidden')
        .attr('x', -9999)
        .attr('y', -9999);

      // 텍스트를 실제 너비에 맞춰 자르는 함수 (앞 글자 보존, 뒤에서 ...)
      const truncateTextByWidth = (text, maxWidth) => {
        const fullText = String(text).replace(/\n/g, ' ');
        tempText.text(fullText);
        const fullWidth = tempText.node().getComputedTextLength();
        
        if (fullWidth <= maxWidth) {
          return fullText;
        }

        // 이진 탐색으로 최대 길이 찾기
        let low = 0;
        let high = fullText.length;
        let bestFit = '';
        
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          const testText = fullText.substring(0, mid) + '...';
          tempText.text(testText);
          const testWidth = tempText.node().getComputedTextLength();
          
          if (testWidth <= maxWidth) {
            bestFit = testText;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }
        
        return bestFit || '...';
      };

      const label = labelsGroup
        .selectAll('text')
        .data(data)
        .enter()
        .append('text')
        .attr('x', 0)
        .attr('y', (_, i) => yPos(i) + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .style('fill', '#6C7687')
        .style('font-size', 13)
        .style('font-family', 'system-ui, -apple-system, sans-serif')
        .style('cursor', 'pointer')
        .text((d) => {
          const fullText = String(d.label).replace(/\n/g, ' ');
          const maxLabelWidth = labelColWidth - 12; // 여백 고려
          return truncateTextByWidth(fullText, maxLabelWidth);
        })
        .on('mouseenter', function(event, d) {
          const fullLabel = String(d.label || '').replace(/\n/g, ' ');
          const value = Number(d.value) || 0;
          tooltip
            .html(`<div>${fullLabel}</div><div style="margin-top:4px;font-size:11px;opacity:0.9;">${value}</div>`)
            .style('opacity', 1);
        })
        .on('mousemove', function(event) {
          tooltip
            .style('left', (event.pageX + 10) + 'px')
            .style('top', (event.pageY - 10) + 'px');
        })
        .on('mouseleave', function() {
          tooltip.style('opacity', 0);
        });

      // 임시 텍스트 요소 제거
      tempText.remove();
    };

    draw();

    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => draw());
        ro.observe(ref.current);
      } else {
        window.addEventListener('resize', draw);
      }
    } catch {
      window.addEventListener('resize', draw);
    }

    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', draw);
        // cleanup: 이 인스턴스의 tooltip 제거
        d3.selectAll(`.department-collaboration-tooltip-${type}`).remove();
      } catch {
        // ignore
      }
    };
  }, [items, max, barHeight, barGap, leftLabelWidth, externalItems, collaborationLoading]);

  return (
    <div className="relative w-full min-h-[200px]">
      <div ref={ref} className="w-full" />
      {(externalItems === null && collaborationLoading) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 text-[#8D9AAF]">
          Loading...
        </div>
      )}
      {(externalItems === null && collaborationErr) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 text-[#F3652D] text-sm">
          {collaborationErr}
        </div>
      )}
      {(externalItems === null && !collaborationLoading && !collaborationErr && (!Array.isArray(items) || items.length === 0)) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] z-10 text-[#8D9AAF] text-sm">
          데이터가 없습니다.
        </div>
      )}
    </div>
  );
}
